import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FuseUtils } from '@fuse/utils';
import { Company, ICompany } from '@shared/models/company';
import { Person } from '@shared/models/person';
import { map, tap } from 'rxjs/operators';
import { BcmApiService } from '@modules/bcm/@shared/services';
import { Boat, IBoat } from '@shared/models/boat';
import { InvoicePosition } from '@shared/models/invoice-position';
import { CompanyContact, ICompanyContact } from '@shared/models/company-contact';
import { BcmDocument, BcmDocumentDto } from '@shared/models/bcm-document';
import { parseHttpResponseItem } from '@shared/functions/parse-http-response';

@Injectable()
export class CompaniesService  {

    private _endpointBaseUrl = `companies`;

    onCompaniesChanged: BehaviorSubject<any>;
    onSearchTextChanged: Subject<any>;
    companies: Company[];
    searchText: string;

    constructor(
        private _bcmApiService: BcmApiService
    ) {
        // Set the defaults
        this.onCompaniesChanged = new BehaviorSubject([]);
        this.onSearchTextChanged = new Subject();

        this.onSearchTextChanged.subscribe(searchText => {
            this.searchText = searchText;
            this.onCompaniesChanged.next(FuseUtils.filterArrayByString(this.companies, searchText));
        });
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<Company[]> {
        return this.getCompanies();
    }

    getCompanies(): Observable<Company[]> {
        return this._bcmApiService.get(this._endpointBaseUrl)
            .pipe(
                map((response: ICompany[]) => {

                    this.companies = response.map(company => new Company(company));

                    if (this.searchText && this.searchText !== '') {
                        this.onCompaniesChanged.next(FuseUtils.filterArrayByString(
                            this.companies,
                            this.searchText,
                            [
                                'name',
                                'form',
                                'fullName',
                                'note',
                                'searchActiveTenantRelations',
                                'fullAddress',
                                'mails',
                                'phones',
                                'identNumber',
                                'IBAN',
                            ],
                        ));
                    } else {
                        this.onCompaniesChanged.next(this.companies);
                    }

                    return this.companies;
                })
            );
    }

    updateCompany(company: Company): Observable<any> {
        return this._bcmApiService.put<Company>(`${this._endpointBaseUrl}/${company.id}`, company);
    }

    addCompany(company: Company): Observable<any> {
        return this._bcmApiService.post<Company>(this._endpointBaseUrl, company);
    }

    deleteCompany(company: Company): Observable<any> {
        return this._bcmApiService.delete<Person>(`${this._endpointBaseUrl}/${company.id}`)
            .pipe(
                tap((response: any) => {
                    if (response) {
                        this.companies = this.companies.filter((currentCompany) => currentCompany.id !== company.id);
                        if (this.searchText && this.searchText !== '') {
                            this.onCompaniesChanged.next(FuseUtils.filterArrayByString(this.companies, this.searchText));
                        } else {
                            this.onCompaniesChanged.next(this.companies);
                        }
                    }
                })
            );
    }

    getContactPersons(companyId): Observable<CompanyContact[]> {
        return this._bcmApiService.get<ICompanyContact[]>(`${this._endpointBaseUrl}/${companyId}/contact-persons`)
            .pipe(
                map((response: ICompanyContact[]) => response.map((companyContactRaw: ICompanyContact) => new CompanyContact(companyContactRaw)))
            );
    }

    addContactPerson(companyId: number, contact: CompanyContact): Observable<any> {
        return this._bcmApiService.post(`${this._endpointBaseUrl}/${companyId}/contact-persons`, contact);
    }

    updateContactPerson(companyId: number, contact: CompanyContact): Observable<any> {
        return this._bcmApiService.put(`${this._endpointBaseUrl}/${companyId}/contact-persons/${contact.id}`, contact);
    }

    deleteContactPerson(companyId: number, contactPersonId: number): Observable<any> {
        return this._bcmApiService.delete(`${this._endpointBaseUrl}/${companyId}/contact-persons/${contactPersonId}`);
    }

    getBoats(companyId): Observable<Boat[]> {
        return this._bcmApiService.get<IBoat[]>(`${this._endpointBaseUrl}/${companyId}/boats`)
            .pipe(
                map((response: IBoat[]) => response.map((boatRaw: IBoat) => new Boat(boatRaw)))
            );
    }

    addBoat(companyId: number, boatId: number, isNewOwnerUntil?: Date): Observable<any> {
        return this._bcmApiService.post(`${this._endpointBaseUrl}/${companyId}/boats`, {boatId, isNewOwnerUntil});
    }

    deleteBoat(companyId: number, boatId: number): Observable<any> {
        return this._bcmApiService.delete(`${this._endpointBaseUrl}/${companyId}/boats/${boatId}`);
    }

    addPosition(companyId: number, position: InvoicePosition): Observable<any> {
        return this._bcmApiService.put<IBoat>(`companies/${companyId}/positions`, {position});
    }

    deletePosition(company: Company, position: InvoicePosition): Observable<any> {
        return this._bcmApiService.delete<any>(`companies/${company.id}/positions/${position.id}`);
    }

    getDocument(companyId: number, documentId: number): Observable<BcmDocument> {
        return this._bcmApiService.get<BcmDocumentDto>(`companies/${companyId}/documents/${documentId}`)
            .pipe(parseHttpResponseItem<BcmDocumentDto, BcmDocument>(BcmDocument));
    }

    addDocument(companyId: number, document: BcmDocument): Observable<BcmDocument> {
        return this._bcmApiService.post<BcmDocumentDto>(`companies/${companyId}/documents`, document)
            .pipe(parseHttpResponseItem<BcmDocumentDto, BcmDocument>(BcmDocument));
    }

    updateDocument(companyId: number, document: BcmDocument, documentData: Partial<BcmDocument>): Observable<BcmDocument> {
        return this._bcmApiService.put<BcmDocumentDto>(`companies/${companyId}/documents/${document.id}`, documentData)
            .pipe(parseHttpResponseItem<BcmDocumentDto, BcmDocument>(BcmDocument));
    }

    deleteDocument(companyId: number, documentId: number): Observable<any> {
        return this._bcmApiService.delete(`companies/${companyId}/documents/${documentId}`);
    }
}
